import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  DialogRoot,
  DialogTrigger,
  Input,
} from '@ibsys/kit';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UpdateProfileRequest, useUpdateMyProfile } from '@/entities/profile';
import { ResetPasswordDialog } from '@/pages/profile/ui/ResetPasswordDialog';
import { EMAIL_REGEX } from '@/shared/lib';

export type MainInfoFormProps = {
  values: UpdateProfileRequest;
};

export const MainInfoForm: FC<MainInfoFormProps> = ({ values }) => {
  const { t } = useTranslation(['common', 'profile']);

  const [isPasswordDialogOpen, setPasswordDialogOpen] = useState(false);

  const { mutateAsync: updateProfile, isPending: isSavingProfile } = useUpdateMyProfile();

  // Пришедший профиль становится умолчаниями формы: «Отменить» после сохранения возвращает к сохранённому
  const methods = useForm<UpdateProfileRequest>({ values, mode: 'onBlur' });

  const { reset, handleSubmit, control } = methods;

  const submit = handleSubmit(async (formValues) => {
    try {
      await updateProfile(formValues);
    } catch {
      // Причину отказа (например, занятый email) показало общее уведомление клиента; введённое остаётся в форме
    }
  });

  return (
    <DialogRoot onOpenChange={setPasswordDialogOpen} open={isPasswordDialogOpen}>
      <Card className={'w-full'}>
        <CardHeader className={'flex gap-3 items-center h-13'}>
          <CardTitle>{t('profile:mainInformation')}</CardTitle>
          <DialogTrigger asChild>
            <Button className={'ml-auto'} variant={'outline'}>
              {t('profile:resetPassword')}
            </Button>
          </DialogTrigger>
        </CardHeader>
        <CardContent className={'flex flex-col gap-3'}>
          <FormProvider {...methods}>
            <Input label={t('profile:name')} control={control} name={'firstName'} />
            <Input label={t('profile:surname')} control={control} name={'lastName'} />
            <Input
              label={t('profile:email')}
              control={control}
              name={'email'}
              rules={{
                pattern: {
                  value: EMAIL_REGEX,
                  message: t('profile:errors.email'),
                },
              }}
            />
            <div className={'flex gap-3 mt-2'}></div>
          </FormProvider>
        </CardContent>
        <CardFooter className={'gap-3 w-full justify-end'}>
          <Button onClick={submit} isLoading={isSavingProfile}>
            {t('save')}
          </Button>
          <Button variant={'secondary'} onClick={() => reset()}>
            {t('cancel')}
          </Button>
        </CardFooter>
      </Card>

      <ResetPasswordDialog close={() => setPasswordDialogOpen(false)} />
    </DialogRoot>
  );
};
